/**
 * useNetworkHierarchyLayer - Hook para visualización de Jerarquía Morfológica
 *
 * Este hook gestiona la visualización de la capa de jerarquía morfológica
 * en el mapa, aplicando una rampa de color según el decil de jerarquía.
 *
 * Usa source/layer de MapLibre para renderizar el GeoJSON de forma eficiente.
 *
 * @example
 * ```typescript
 * // En el componente MapLayout o similar
 * const { data } = useNetworkHierarchy(projectId);
 * useNetworkHierarchyLayer({
 *   data,
 *   visible: hierarchyLayerVisible,
 * });
 *
 * // El hook automáticamente:
 * // 1. Crea/actualiza el source con el GeoJSON
 * // 2. Aplica rampa de color interpolada por decil
 * // 3. Muestra/oculta según visibilidad
 * ```
 */

import { useEffect, useRef, useCallback } from 'react';
import { mapEngine } from '../../map-engine/MapEngineService';
import type { NetworkHierarchyResponse } from '../services/network-hierarchy-api.service';
import { COLOR_RAMPS } from '../config/syntax-color-ramps';
import type { MetricRange } from '../services/syntax-api.service';

// ============================================================================
// CONSTANTS
// ============================================================================

const SOURCE_ID = 'network-hierarchy';
const LAYER_ID = 'network-hierarchy-deciles';
const CANONICAL_EDGES_LAYER_ID = 'canonical-network-edges';
const CANONICAL_EDGES_PREVIEW_LAYER_ID = 'canonical-network-edges-preview';
const CANONICAL_NODES_PREVIEW_LAYER_ID = 'canonical-network-nodes-preview';
const DEFAULT_PERCENTILE_RANGE: MetricRange = { min: 0, max: 1 };
const MIN_VISUAL_SPAN = 0.0001;

/**
 * Rampa de color para deciles de jerarquía (1-10)
 * Usando 'viridis': azul oscuro (baja) → amarillo brillante (alta)
 */
const HIERARCHY_COLOR_RAMP = COLOR_RAMPS.viridis;

// ============================================================================
// HOOK
// ============================================================================

export interface UseNetworkHierarchyLayerConfig {
  /** Datos del análisis (GeoJSON FeatureCollection) */
  data: NetworkHierarchyResponse | null;
  /** Si la capa debe ser visible */
  visible: boolean;
  /** Rango visual aplicado al score derivado */
  visualRange?: MetricRange | null;
  /** Contraste aplicado al score derivado */
  contrastExponent?: number;
}

function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function getStructureDataRange(data: NetworkHierarchyResponse | null): MetricRange | null {
  if (!data?.features.length) {
    return null;
  }

  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const feature of data.features) {
    const value = coerceFiniteNumber(feature.properties.combinedPercentile);
    if (value !== null) {
      min = Math.min(min, value);
      max = Math.max(max, value);
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }

  if (min === max) {
    return {
      min: Math.max(0, min - 0.01),
      max: Math.min(1, max + 0.01),
    };
  }

  return { min, max };
}

function clampVisualRange(
  dataRange: MetricRange | null,
  override: MetricRange | null | undefined,
): MetricRange | null {
  if (!dataRange) {
    return null;
  }

  if (!override) {
    return dataRange;
  }

  const min = Math.max(dataRange.min, Math.min(override.min, override.max));
  const max = Math.min(dataRange.max, Math.max(override.min, override.max));

  if (max - min < MIN_VISUAL_SPAN) {
    return dataRange;
  }

  return { min, max };
}

function buildHierarchyColorExpression(
  activeVisualRange: MetricRange | null,
  contrastExponent: number,
): unknown[] {
  const safeRange = activeVisualRange ?? DEFAULT_PERCENTILE_RANGE;
  const safeSpan = Math.max(safeRange.max - safeRange.min, MIN_VISUAL_SPAN);
  const safeContrast = Number.isFinite(contrastExponent) && contrastExponent > 0
    ? contrastExponent
    : 1;

  return [
    'interpolate',
    ['linear'],
    ['coalesce', ['to-number', ['get', 'combinedPercentile']], safeRange.min],
    ...HIERARCHY_COLOR_RAMP.stops.flatMap(({ stop, color }) => [
      safeRange.min + Math.pow(stop, 1 / safeContrast) * safeSpan,
      color,
    ]),
  ];
}

function buildHierarchyDecileExpression(): unknown[] {
  return [
    'interpolate',
    ['linear'],
    ['/', ['-', ['coalesce', ['to-number', ['get', 'hierarchyDecile']], 1], 1], 9],
    ...HIERARCHY_COLOR_RAMP.stops.flatMap(({ stop, color }) => [stop, color]),
  ];
}

function calculateVisualScore(
  rawValue: number,
  activeVisualRange: MetricRange,
  contrastExponent: number,
): number {
  const safeSpan = Math.max(activeVisualRange.max - activeVisualRange.min, MIN_VISUAL_SPAN);
  const safeContrast = Number.isFinite(contrastExponent) && contrastExponent > 0
    ? contrastExponent
    : 1;
  const normalized = Math.min(
    Math.max((rawValue - activeVisualRange.min) / safeSpan, 0),
    1,
  );

  return Math.pow(normalized, safeContrast);
}

function logHierarchyVisualizationDiagnostics(
  data: NetworkHierarchyResponse,
  dataRange: MetricRange | null,
  activeVisualRange: MetricRange | null,
  contrastExponent: number,
): void {
  let numberCount = 0;
  let stringCount = 0;
  let nullishCount = 0;
  let invalidCount = 0;
  let rawMin = Number.POSITIVE_INFINITY;
  let rawMax = Number.NEGATIVE_INFINITY;
  let scoreMin = Number.POSITIVE_INFINITY;
  let scoreMax = Number.NEGATIVE_INFINITY;
  let zeroCount = 0;
  let oneCount = 0;
  let intermediateCount = 0;
  let belowRangeCount = 0;
  let aboveRangeCount = 0;
  const sample: Array<{ edgeId: string; rawValue: number; visualScore: number }> = [];

  for (const feature of data.features) {
    const originalValue = feature.properties.combinedPercentile;

    if (typeof originalValue === 'number') {
      if (Number.isFinite(originalValue)) {
        numberCount += 1;
      } else {
        invalidCount += 1;
      }
    } else if (typeof originalValue === 'string') {
      if (originalValue.trim() === '') {
        invalidCount += 1;
      } else {
        const parsed = Number(originalValue);
        if (Number.isFinite(parsed)) {
          stringCount += 1;
        } else {
          invalidCount += 1;
        }
      }
    } else if (originalValue === null || originalValue === undefined) {
      nullishCount += 1;
    } else {
      invalidCount += 1;
    }

    const rawValue = coerceFiniteNumber(originalValue);
    if (rawValue === null) {
      continue;
    }

    rawMin = Math.min(rawMin, rawValue);
    rawMax = Math.max(rawMax, rawValue);

    if (!activeVisualRange) {
      continue;
    }

    if (rawValue < activeVisualRange.min) {
      belowRangeCount += 1;
    } else if (rawValue > activeVisualRange.max) {
      aboveRangeCount += 1;
    }

    const visualScore = calculateVisualScore(rawValue, activeVisualRange, contrastExponent);
    scoreMin = Math.min(scoreMin, visualScore);
    scoreMax = Math.max(scoreMax, visualScore);

    if (visualScore <= 0.000001) {
      zeroCount += 1;
    } else if (visualScore >= 0.999999) {
      oneCount += 1;
    } else {
      intermediateCount += 1;
    }

    if (sample.length < 5) {
      sample.push({
        edgeId: feature.properties.edgeId,
        rawValue,
        visualScore,
      });
    }
  }

  console.log('[useNetworkHierarchyLayer] Visualization diagnostics:', {
    profile: data.metadata.analysisProfile,
    layerProperty: 'combinedPercentile',
    dataRange,
    activeVisualRange,
    contrastExponent,
    rawValueStats: {
      min: Number.isFinite(rawMin) ? rawMin : null,
      max: Number.isFinite(rawMax) ? rawMax : null,
      numbers: numberCount,
      numericStrings: stringCount,
      nullish: nullishCount,
      invalid: invalidCount,
    },
    visualScoreStats: {
      min: Number.isFinite(scoreMin) ? scoreMin : null,
      max: Number.isFinite(scoreMax) ? scoreMax : null,
      zeroCount,
      oneCount,
      intermediateCount,
      belowRangeCount,
      aboveRangeCount,
    },
    sample,
  });
}

function getLayerIndex(map: maplibregl.Map, layerId: string): number | null {
  const layers = map.getStyle()?.layers ?? [];
  const index = layers.findIndex((layer) => layer.id === layerId);
  return index >= 0 ? index : null;
}

function getRelevantLayerOrder(map: maplibregl.Map): Record<string, number | null> {
  const layers = map.getStyle()?.layers ?? [];
  const firstBaseRoadLayer = layers.find((layer) => layer.id.startsWith('base-road-network-layer-'));

  return {
    hierarchy: getLayerIndex(map, LAYER_ID),
    canonicalEdges: getLayerIndex(map, CANONICAL_EDGES_LAYER_ID),
    canonicalEdgesPreview: getLayerIndex(map, CANONICAL_EDGES_PREVIEW_LAYER_ID),
    canonicalNodesPreview: getLayerIndex(map, CANONICAL_NODES_PREVIEW_LAYER_ID),
    firstBaseRoadLayer: firstBaseRoadLayer ? getLayerIndex(map, firstBaseRoadLayer.id) : null,
  };
}

/**
 * Hook para visualización de jerarquía morfológica en el mapa
 *
 * Crea un source GeoJSON y un layer con rampa de color por decil.
 * Se actualiza automáticamente cuando cambian los datos o la visibilidad.
 */
export function useNetworkHierarchyLayer(config: UseNetworkHierarchyLayerConfig): void {
  const {
    data,
    visible,
    visualRange = null,
    contrastExponent = 1,
  } = config;

  // Ref para tracking de estado
  const sourceAddedRef = useRef(false);
  const layerAddedRef = useRef(false);
  const interactionListenersRegisteredRef = useRef(false);
  const styleListenerRegisteredRef = useRef(false);
  const dataRef = useRef<NetworkHierarchyResponse | null>(data);
  const visibleRef = useRef(visible);
  const visualRangeRef = useRef<MetricRange | null>(visualRange);
  const contrastExponentRef = useRef(contrastExponent);
  // Throttle para handleStyleData: evita loop moveLayer → styledata → syncLayerStack → moveLayer
  const lastStyleSyncRef = useRef<number>(0);

  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  useEffect(() => {
    visualRangeRef.current = visualRange;
  }, [visualRange]);

  useEffect(() => {
    contrastExponentRef.current = contrastExponent;
  }, [contrastExponent]);

  /**
   * Crea el source GeoJSON si no existe
   */
  const ensureSource = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) {
      console.warn('[useNetworkHierarchyLayer] No map available');
      return false;
    }

    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: {
          type: 'FeatureCollection',
          features: [],
        },
      });
      sourceAddedRef.current = true;
      console.log('[useNetworkHierarchyLayer] Source created:', SOURCE_ID);
    }

    return true;
  }, []);

  /**
   * Crea el layer con rampa de color si no existe
   */
  const ensureLayer = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) return false;

    if (!map.getLayer(LAYER_ID)) {
      const colorExpr = buildHierarchyDecileExpression() as any;
      const beforeId = map.getLayer(CANONICAL_EDGES_PREVIEW_LAYER_ID)
        ? CANONICAL_EDGES_PREVIEW_LAYER_ID
        : map.getLayer(CANONICAL_NODES_PREVIEW_LAYER_ID)
          ? CANONICAL_NODES_PREVIEW_LAYER_ID
          : undefined;

      map.addLayer({
        id: LAYER_ID,
        type: 'line',
        source: SOURCE_ID,
        paint: {
          'line-color': colorExpr,
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            10, 1.5,
            14, 3,
            18, 6,
          ],
          'line-opacity': 0.85,
        },
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
      }, beforeId);

      layerAddedRef.current = true;
      console.log('[useNetworkHierarchyLayer] Layer created:', {
        layerId: LAYER_ID,
        beforeId: beforeId ?? 'top',
        colorRamp: HIERARCHY_COLOR_RAMP.name,
        order: getRelevantLayerOrder(map),
      });
    }

    return true;
  }, []);

  const ensureLayerOrder = useCallback((reason: string) => {
    const map = mapEngine.getMap();
    if (!map || !map.getLayer(LAYER_ID)) {
      return;
    }

    // FIX: Skip reordering when layer is invisible.
    // During initial load the hierarchy layer has visibility='none'. Calling moveLayer()
    // emits styledata, which even with the idempotency check below can loop: every time
    // useBaseNetworkLayer adds its 20 layers above hierarchy, hierarchy is no longer last
    // and would call moveLayer again on the next styledata event.
    // Layer order only matters when the layer is actually rendered (visible).
    const currentVisibility = map.getLayoutProperty(LAYER_ID, 'visibility');
    if (!currentVisibility || currentVisibility === 'none') {
      return;
    }

    const beforeId = map.getLayer(CANONICAL_EDGES_PREVIEW_LAYER_ID)
      ? CANONICAL_EDGES_PREVIEW_LAYER_ID
      : map.getLayer(CANONICAL_NODES_PREVIEW_LAYER_ID)
        ? CANONICAL_NODES_PREVIEW_LAYER_ID
        : undefined;

    // Idempotency check (belt-and-suspenders for when layer IS visible).
    const layers = map.getStyle()?.layers ?? [];
    const hierarchyIdx = layers.findIndex((l) => l.id === LAYER_ID);

    if (beforeId) {
      const beforeIdx = layers.findIndex((l) => l.id === beforeId);
      if (hierarchyIdx >= 0 && beforeIdx >= 0 && hierarchyIdx === beforeIdx - 1) {
        return; // Already directly before beforeId — no move needed
      }
      map.moveLayer(LAYER_ID, beforeId);
    } else {
      if (hierarchyIdx >= 0 && hierarchyIdx === layers.length - 1) {
        return; // Already at the top of the stack — no move needed
      }
      map.moveLayer(LAYER_ID);
    }

    console.log('[useNetworkHierarchyLayer] Layer order ensured:', {
      reason,
      movedLayer: LAYER_ID,
      beforeId: beforeId ?? 'top',
      order: getRelevantLayerOrder(map),
    });
  }, []);

  const handleLayerClick = useCallback((e: any) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const hierarchyData = feature.properties;

      window.dispatchEvent(
        new CustomEvent('hierarchy-segment-click', {
          detail: {
            edgeId: hierarchyData?.edgeId,
            hierarchyData,
          },
        })
      );

      console.log('[useNetworkHierarchyLayer] Segment clicked:', hierarchyData?.edgeId);
    }
  }, []);

  const handleLayerMouseEnter = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) return;
    map.getCanvas().style.cursor = 'pointer';
  }, []);

  const handleLayerMouseLeave = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) return;
    map.getCanvas().style.cursor = '';
  }, []);

  const registerInteractions = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || interactionListenersRegisteredRef.current) {
      return;
    }

    map.on('click', LAYER_ID, handleLayerClick);
    map.on('mouseenter', LAYER_ID, handleLayerMouseEnter);
    map.on('mouseleave', LAYER_ID, handleLayerMouseLeave);
    interactionListenersRegisteredRef.current = true;
  }, [handleLayerClick, handleLayerMouseEnter, handleLayerMouseLeave]);

  const unregisterInteractions = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map || !interactionListenersRegisteredRef.current) {
      return;
    }

    map.off('click', LAYER_ID, handleLayerClick);
    map.off('mouseenter', LAYER_ID, handleLayerMouseEnter);
    map.off('mouseleave', LAYER_ID, handleLayerMouseLeave);
    interactionListenersRegisteredRef.current = false;
  }, [handleLayerClick, handleLayerMouseEnter, handleLayerMouseLeave]);

  /**
   * Actualiza los datos del source
   */
  const updateSourceData = useCallback(() => {
    const map = mapEngine.getMap();
    const currentData = dataRef.current;
    if (!map || !currentData) return;

    const source = map.getSource(SOURCE_ID);
    if (!source || source.type !== 'geojson') {
      console.warn('[useNetworkHierarchyLayer] Cannot update source data because source is missing:', {
        sourceId: SOURCE_ID,
        layerExists: !!map.getLayer(LAYER_ID),
        visible: visibleRef.current,
      });
      return;
    }

    (source as maplibregl.GeoJSONSource).setData(currentData as any);

    console.log(
      '[useNetworkHierarchyLayer] Source data updated:',
      currentData.features.length,
      'features',
      '| profile:',
      currentData.metadata.analysisProfile,
    );
  }, []);

  const updateColorExpression = useCallback(() => {
    const map = mapEngine.getMap();
    const currentData = dataRef.current;
    if (!map || !map.getLayer(LAYER_ID)) {
      if (visibleRef.current) {
        console.warn('[useNetworkHierarchyLayer] Cannot update color because layer is missing while visible=true:', {
          layerId: LAYER_ID,
          sourceExists: !!map?.getSource(SOURCE_ID),
          order: map ? getRelevantLayerOrder(map) : null,
        });
      }
      return;
    }

    const dataRange = getStructureDataRange(currentData);
    const activeVisualRange = clampVisualRange(dataRange, visualRangeRef.current);
    const currentContrastExponent = contrastExponentRef.current;

    if (!currentData || !dataRange || !activeVisualRange) {
      map.setPaintProperty(
        LAYER_ID,
        'line-color',
        buildHierarchyDecileExpression() as any,
      );

      if (currentData) {
        console.warn(
          '[useNetworkHierarchyLayer] Falling back to hierarchyDecile color expression because combinedPercentile range is not usable.',
        );
        logHierarchyVisualizationDiagnostics(
          currentData,
          dataRange,
          activeVisualRange,
          currentContrastExponent,
        );
      }

      return;
    }

    map.setPaintProperty(
      LAYER_ID,
      'line-color',
      buildHierarchyColorExpression(activeVisualRange, currentContrastExponent) as any,
    );

    logHierarchyVisualizationDiagnostics(
      currentData,
      dataRange,
      activeVisualRange,
      currentContrastExponent,
    );
  }, []);

  /**
   * Muestra/oculta el layer según visibilidad
   */
  const updateVisibility = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const layer = map.getLayer(LAYER_ID);
    if (!layer) {
      console.warn('[useNetworkHierarchyLayer] Visibility sync skipped because layer is missing:', {
        requestedVisibility: visibleRef.current ? 'visible' : 'none',
        sourceExists: !!map.getSource(SOURCE_ID),
        order: getRelevantLayerOrder(map),
      });
      return;
    }

    const newVisibility = visibleRef.current ? 'visible' : 'none';
    const currentVisibility = map.getLayoutProperty(LAYER_ID, 'visibility');

    if (currentVisibility !== newVisibility) {
      map.setLayoutProperty(LAYER_ID, 'visibility', newVisibility);
      console.log('[useNetworkHierarchyLayer] Visibility changed:', {
        layerId: LAYER_ID,
        visibility: newVisibility,
        order: getRelevantLayerOrder(map),
      });
    }
  }, []);

  /**
   * Limpia source y layer al desmontar
   */
  const cleanup = useCallback(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    if (layerAddedRef.current && map.getLayer(LAYER_ID)) {
      map.removeLayer(LAYER_ID);
      layerAddedRef.current = false;
      console.log('[useNetworkHierarchyLayer] Layer removed:', LAYER_ID);
    }

    if (sourceAddedRef.current && map.getSource(SOURCE_ID)) {
      map.removeSource(SOURCE_ID);
      sourceAddedRef.current = false;
      console.log('[useNetworkHierarchyLayer] Source removed:', SOURCE_ID);
    }
  }, []);

  const syncLayerStack = useCallback((reason = 'sync') => {
    const map = mapEngine.getMap();
    if (!map || !map.isStyleLoaded()) {
      return;
    }

    if (!map.getSource(SOURCE_ID)) {
      sourceAddedRef.current = false;
    }

    if (!map.getLayer(LAYER_ID)) {
      layerAddedRef.current = false;
    }

    ensureSource();
    ensureLayer();
    ensureLayerOrder(reason);
    registerInteractions();
    // Skip data/color/visibility when syncing from a styledata event:
    // setPaintProperty and setData both emit styledata, which would re-trigger
    // this function and create a secondary loop (throttled to 300ms, but still
    // unnecessary churn). Data, color, and visibility are managed by their own
    // React effects that fire on the correct state changes.
    if (reason !== 'styledata') {
      updateSourceData();
      updateColorExpression();
      updateVisibility();
    }
    console.log('[useNetworkHierarchyLayer] Layer stack synchronized:', {
      reason,
      sourceExists: !!map.getSource(SOURCE_ID),
      layerExists: !!map.getLayer(LAYER_ID),
      requestedVisible: visibleRef.current,
      actualVisibility: map.getLayer(LAYER_ID)
        ? map.getLayoutProperty(LAYER_ID, 'visibility')
        : 'missing',
      order: getRelevantLayerOrder(map),
    });
  }, [
    ensureLayerOrder,
    ensureLayer,
    ensureSource,
    registerInteractions,
    updateColorExpression,
    updateSourceData,
    updateVisibility,
  ]);

  // ── Efecto principal: setup inicial + recreación solo cuando cambia el estilo del mapa ──
  useEffect(() => {
    const handleStyleData = (event: any): void => {
      if (event?.dataType && event.dataType !== 'style') {
        return;
      }

      // Throttle: 300ms minimum between syncs.
      // Belt-and-suspenders guard alongside the idempotency check in ensureLayerOrder.
      const now = Date.now();
      if (now - lastStyleSyncRef.current < 300) return;
      lastStyleSyncRef.current = now;

      console.log('[useNetworkHierarchyLayer] styledata detected, checking hierarchy layer stack');
      syncLayerStack('styledata');
    };

    const attachToMap = (map: maplibregl.Map): void => {
      if (!styleListenerRegisteredRef.current) {
        map.on('styledata', handleStyleData);
        styleListenerRegisteredRef.current = true;
      }

      if (map.isStyleLoaded()) {
        syncLayerStack('initial-attach');
      } else {
        map.once('load', () => syncLayerStack('map-load'));
      }
    };

    let pollInterval: NodeJS.Timeout | null = null;
    const tryAttach = (): boolean => {
      const map = mapEngine.getMap();
      if (!map) {
        return false;
      }

      attachToMap(map);
      return true;
    };

    if (!tryAttach()) {
      pollInterval = setInterval(() => {
        if (tryAttach() && pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      }, 100);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      const map = mapEngine.getMap();
      if (map && styleListenerRegisteredRef.current) {
        map.off('styledata', handleStyleData);
        styleListenerRegisteredRef.current = false;
      }
      unregisterInteractions();
      cleanup();
    };
  }, [cleanup, syncLayerStack, unregisterInteractions]);

  // ── Efecto: actualizar datos cuando cambian ──
  useEffect(() => {
    syncLayerStack('data-change');
    updateSourceData();
    updateColorExpression();
  }, [data, syncLayerStack, updateColorExpression, updateSourceData]);

  // ── Efecto: actualizar color cuando cambian rango visual o contraste ──
  useEffect(() => {
    if (visible) {
      syncLayerStack('visual-settings-change');
    }
    updateColorExpression();
  }, [contrastExponent, syncLayerStack, updateColorExpression, visualRange, visible]);

  // ── Efecto: actualizar visibilidad cuando cambia ──
  useEffect(() => {
    if (visible) {
      syncLayerStack('visibility-on');
      ensureLayerOrder('visibility-on');
    }
    updateVisibility();
  }, [ensureLayerOrder, syncLayerStack, updateVisibility, visible]);
}

export default useNetworkHierarchyLayer;
